import {
  Datagrid,
  FunctionField,
  ListContextProvider,
  Loading,
  NumberField,
  SimpleShowLayout,
  TextField,
  useGetList,
  useList,
  useTranslate
} from 'react-admin'
import {
  Axis,
  BarGroup,
  BarSeries,
  Grid,
  GlyphSeries,
  XYChart,
  darkTheme,
  lightTheme
} from '@visx/xychart'
import { LegendOrdinal } from '@visx/legend'
import { scaleOrdinal } from '@visx/scale'
import {
  addDays,
  addMinutes,
  subDays,
  eachMinuteOfIntervalWithOptions,
  format,
  parse,
  parseISO
} from 'date-fns/fp'
import React from 'react'
import { flow, get, isEmpty, isNil, map, max, replace, round } from 'lodash/fp'
import log from 'loglevel'
import {
  IconButton,
  Grid as MuiGrid,
  Typography,
  useMediaQuery
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'

import { Pagination } from '../Util'
import { Activity, Frame, NotificationKind } from '../../types'

log.setLevel(log.levels.DEBUG)

const ElectricityUsageLegend: React.FC<{ kind: NotificationKind }> = ({
  kind
}) => {
  const translate = useTranslate()
  const colorScale = scaleOrdinal<string, string>({
    domain:
      kind === NotificationKind.inactive
        ? [
            translate('mine.chart.electricityUsage'),
            translate('mine.chart.activity')
          ]
        : [
            translate('mine.chart.electricityUsageBefore'),
            translate('mine.chart.electricityUsage')
          ],
    range:
      kind === NotificationKind.inactive
        ? ['orange', 'blue']
        : ['#1e90ff', 'orange']
  })
  return (
    <LegendOrdinal scale={colorScale} direction="row" labelMargin="0 1em 0 0" />
  )
}
const ElectricityUsageTable: React.FC<{
  data: any[]
  kind: NotificationKind
}> = ({ kind, data }) => {
  const translate = useTranslate()
  const activityString = {
    active: translate('mine.activity.active'),
    inactive: translate('mine.activity.inactive')
  }
  const listContext = useList({
    data,
    page: 1,
    perPage: 12,
    sort: { field: 'time', order: 'ASC' }
  })
  return (
    <SimpleShowLayout>
      <ListContextProvider value={listContext}>
        <Datagrid bulkActionButtons={false}>
          <TextField source="time" />
          <NumberField source="amount" />
          {kind === NotificationKind.inactive && (
            <FunctionField
              source="activity"
              render={(record: Frame) => (
                <Typography variant="subtitle1">
                  {activityString[record.activity as Activity]}
                </Typography>
              )}
            />
          )}
        </Datagrid>
        <Pagination />
      </ListContextProvider>
    </SimpleShowLayout>
  )
}

export const ElectricityUsageChart: React.FC<{
  spin: string
  date: string
  kind: NotificationKind
  showTable?: boolean
}> = ({ spin, date, kind, showTable }) => {
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('md'))
  const [displayDate, setDisplayDate] = React.useState(date)
  const [limitDate, setLimitDate] = React.useState('1970-01-01')
  const translate = useTranslate()
  const displayDateBefore = format('yyyy-MM-dd')(
    subDays(1)(parse(Date.now(), 'yyyy-MM-dd')(displayDate))
  )
  const { data: electricityUsages, isLoading } = useGetList(
    'electricityUsages',
    {
      filter: {
        spin,
        date: { eq: displayDate }
      },
      pagination: { page: 1, perPage: 10 },
      sort: { field: 'name', order: 'DESC' }
    }
  )
  const { data: electricityUsagesBefore, isLoading: isLoadingBefore } =
    useGetList('electricityUsages', {
      filter: {
        spin,
        date: { eq: displayDateBefore }
      },
      pagination: { page: 1, perPage: 10 },
      sort: { field: 'name', order: 'DESC' }
    })
  if (isLoading || isLoadingBefore) return <Loading />
  const prev = (): void => {
    setDisplayDate(
      format('yyyy-MM-dd')(subDays(1)(parseISO(`${displayDate}T00:00`)))
    )
  }
  const next = (): void => {
    setDisplayDate(
      format('yyyy-MM-dd')(addDays(1)(parseISO(`${displayDate}T00:00`)))
    )
  }
  if (isNil(electricityUsages) || isEmpty(electricityUsages)) {
    if (displayDate !== format('yyyy-MM-dd')(Date.now())) {
      setLimitDate(
        format('yyyy-MM-dd')(addDays(1)(parseISO(`${displayDate}T00:00`)))
      )
      next()
    }
    return <></>
  }
  const data =
    kind === NotificationKind.inactive
      ? electricityUsages[0].frames
      : map((frame: Frame) => {
          const time = format('HH:mm')(
            addMinutes(15)(parse(Date.now(), 'HH:mm')(frame.time))
          )
          return { ...frame, time }
        })(electricityUsages[0].frames)
  const { frames: dataBefore } =
    isNil(electricityUsagesBefore) || isEmpty(electricityUsagesBefore)
      ? []
      : electricityUsagesBefore[0]
  const chartColors =
    kind === NotificationKind.inactive || isEmpty(dataBefore)
      ? ['orange']
      : ['#1e90ff', 'orange']
  const theme = window.matchMedia('(prefers-color-scheme: light)').matches
    ? { ...lightTheme, colors: chartColors }
    : /* istanbul ignore next: matchMedia is mocked to return always `true` */
      { ...darkTheme, colors: chartColors }
  const width = isDesktop ? window.innerWidth * 0.5 : window.innerWidth * 0.9
  const height = width // window.innerHeight * 0.3
  const xScaleDomain = map(format('HH:mm'))(
    eachMinuteOfIntervalWithOptions({
      step: kind === NotificationKind.inactive ? 30 : 15
    })({
      start: parseISO('1970-01-01T00:00'),
      end: parseISO('1970-01-01T23:45')
    })
  )
  const eMax = flow([map('amount'), max])(data)
  const yMax = eMax < 0.5 ? 0.5 : round(eMax * 12) / 10
  const active = 1
  const inactive = 0
  const activityTicks = 1
  return (
    <>
      <MuiGrid container spacing="1em">
        {!isNil(showTable) && showTable && (
          <MuiGrid item xs={isDesktop ? 3.5 : 12}>
            <ElectricityUsageTable kind={kind} data={data} />
          </MuiGrid>
        )}
        <MuiGrid item xs={isDesktop ? 5.5 : 12}>
          <MuiGrid container justifyContent="space-between">
            <MuiGrid item xs={1}>
              <IconButton
                onClick={prev}
                disabled={
                  displayDate ===
                    format('yyyy-MM-dd')(
                      subDays(2)(parseISO(`${date}T00:00`))
                    ) || displayDate === limitDate
                }>
                <ArrowBackIcon />
              </IconButton>
            </MuiGrid>
            <MuiGrid
              item
              xs={5}
              sx={{ marginTop: '0.3em', marginLeft: '0.8em' }}>
              <Typography variant="subtitle1">
                {format('yyyy年M月d日')(parseISO(`${displayDate}T00:00`))}
              </Typography>
            </MuiGrid>
            <MuiGrid item xs={1}>
              <IconButton onClick={next} disabled={displayDate === date}>
                <ArrowForwardIcon />
              </IconButton>
            </MuiGrid>
          </MuiGrid>
          <XYChart
            height={height}
            width={width}
            theme={theme}
            margin={
              isDesktop
                ? { top: 50, right: 60, bottom: 50, left: 60 }
                : { top: 50, right: 40, bottom: 25, left: 20 }
            }
            xScale={{ type: 'band', domain: xScaleDomain }}
            yScale={{ type: 'linear', domain: [0, yMax] }}>
            <Axis
              orientation="bottom"
              tickFormat={replace(/^0/)('')}
              numTicks={isDesktop ? 8 : 6}
            />
            {/* <Axis orientation="bottom" left={-30} axisClassName="bottom-line" /> */}
            <Axis
              orientation="right"
              numTicks={8}
              label="kWh"
              labelProps={{
                x: 20,
                y: 30,
                fontSize: 'small',
                textAnchor: 'middle',
                verticalAnchor: 'end',
                style: { transform: `rotate(0deg)` }
              }}
              tickLabelProps={() => ({
                fill: 'black'
              })}
            />
            {kind === NotificationKind.inactive && (
              <Axis
                orientation="left"
                numTicks={activityTicks}
                label={translate('mine.chart.activity')}
                labelProps={{
                  x: -30,
                  y: 30,
                  fontSize: 'small',
                  textAnchor: 'middle',
                  verticalAnchor: 'end',
                  style: { transform: `rotate(0deg)` }
                }}
                tickFormat={(d) =>
                  d === yMax * active
                    ? translate('mine.chart.active')
                    : d === yMax * inactive
                    ? translate('mine.chart.inactive')
                    : ''
                }
                tickLabelProps={() => ({
                  fill: 'black'
                })}
              />
            )}
            {/* <Grid numTicks={8} /> */}
            <Grid numTicks={8} columns={false} />
            {kind === NotificationKind.low && !isEmpty(dataBefore) && (
              <BarGroup padding={0.3}>
                <BarSeries
                  data={dataBefore}
                  dataKey="Line 2"
                  xAccessor={get('time')}
                  yAccessor={get('amount')}
                />
              </BarGroup>
            )}
            <BarGroup padding={0.2}>
              <BarSeries
                data={data}
                dataKey="Line 1"
                xAccessor={get('time')}
                yAccessor={get('amount')}
              />
            </BarGroup>
            {kind === NotificationKind.inactive && (
              <GlyphSeries
                data={data}
                dataKey="Line 2"
                xAccessor={get('time')}
                yAccessor={(d) =>
                  get('activity')(d) === 'active'
                    ? yMax * active
                    : get('activity')(d) === 'inactive'
                    ? yMax * inactive
                    : -1
                }
                colorAccessor={() => 'blue'}
              />
            )}
          </XYChart>
          <MuiGrid container>
            <MuiGrid item sx={{ marginLeft: 'auto', marginRigt: '0' }}>
              <ElectricityUsageLegend kind={kind} />
            </MuiGrid>
          </MuiGrid>
        </MuiGrid>
      </MuiGrid>
    </>
  )
}
